// Customer Management React Query Hooks
import { useQuery, useMutation, useQueryClient, useQueries } from "react-query";
import { useSnackbar } from "notistack";
import {
  APIGetCustomers,
  APIGetCustomerById,
  APIGetCustomerByCode,
  APIGetCustomersByFranchise,
  APICreateCustomer,
  APIUpdateCustomer,
  APIDeleteCustomer,
} from "../../service/api/APICustomer";
import {
  ICustomer,
  ICustomerCreateRequest,
  ICustomerUpdateRequest,
  ICustomerQueryParams,
  ICustomerResponse,
} from "../../compiler/customer.interface";

// Query Keys
export const EQueryKeys = {
  GET_CUSTOMERS: "GET_CUSTOMERS",
  GET_ALL_CUSTOMERS: "GET_ALL_CUSTOMERS",
  GET_CUSTOMER_BY_ID: "GET_CUSTOMER_BY_ID",
  GET_CUSTOMER_BY_CODE: "GET_CUSTOMER_BY_CODE",
  GET_CUSTOMERS_BY_FRANCHISE: "GET_CUSTOMERS_BY_FRANCHISE",
} as const;

interface UseGetAllCustomersOptions {
  enabled?: boolean;
  baseParams?: Omit<ICustomerQueryParams, "page" | "limit">;
  maxPages?: number;
}

// Get all customers with pagination and filtering
export const useGetCustomers = (params?: ICustomerQueryParams) => {
  const { enqueueSnackbar } = useSnackbar();

  return useQuery([EQueryKeys.GET_CUSTOMERS, params], () => APIGetCustomers(params), {
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    onError: (error: any) => {
      console.error("Error fetching customers:", error);
      enqueueSnackbar("เกิดข้อผิดพลาดในการโหลดข้อมูลลูกค้า", { variant: "error" });
    },
  });
};

export const useGetAllCustomers = (options: UseGetAllCustomersOptions = {}) => {
  const { enabled = true, baseParams = {}, maxPages = 20 } = options;
  const { enqueueSnackbar } = useSnackbar();
  const limit = 100;
  const firstPageParams = { ...baseParams, page: 1, limit };

  const firstPageQuery = useQuery(
    [EQueryKeys.GET_ALL_CUSTOMERS, firstPageParams],
    () => APIGetCustomers(firstPageParams),
    {
      enabled,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      onError: (error: any) => {
        console.error("Error fetching customers:", error);
        enqueueSnackbar("เกิดข้อผิดพลาดในการโหลดข้อมูลลูกค้า", { variant: "error" });
      },
    }
  );

  const totalPages = Math.min(firstPageQuery.data?.pagination?.totalPages || 1, maxPages);
  const additionalQueries = useQueries(
    Array.from({ length: Math.max(0, totalPages - 1) }, (_, index) => {
      const page = index + 2;
      const params = { ...baseParams, page, limit };
      return {
        queryKey: [EQueryKeys.GET_ALL_CUSTOMERS, params],
        queryFn: () => APIGetCustomers(params),
        enabled: enabled && !!firstPageQuery.data && totalPages > 1,
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
      };
    })
  ) as Array<{
    data?: ICustomerResponse;
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
    refetch?: () => unknown;
  }>;

  const customers = [
    ...(firstPageQuery.data?.data || []),
    ...additionalQueries.flatMap((query) => query.data?.data || []),
  ];
  const isAdditionalLoading = additionalQueries.some((query) => query.isLoading);
  const isAdditionalFetching = additionalQueries.some((query) => query.isFetching);
  const isAdditionalError = additionalQueries.some((query) => query.isError);

  return {
    data: firstPageQuery.data
      ? {
          data: customers,
          pagination: {
            ...firstPageQuery.data.pagination,
            page: 1,
            limit,
            total: firstPageQuery.data.pagination.total,
            totalPages,
          },
        }
      : undefined,
    isLoading: firstPageQuery.isLoading || isAdditionalLoading,
    isFetching: firstPageQuery.isFetching || isAdditionalFetching,
    isError: firstPageQuery.isError || isAdditionalError,
    refetch: () =>
      Promise.all([
        firstPageQuery.refetch(),
        ...additionalQueries.map((query) => query.refetch?.()),
      ]),
  };
};

// Get customer by UUID
export const useGetCustomerById = (uuid: string, enabled: boolean = true) => {
  const { enqueueSnackbar } = useSnackbar();

  return useQuery([EQueryKeys.GET_CUSTOMER_BY_ID, uuid], () => APIGetCustomerById(uuid), {
    enabled: enabled && !!uuid,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    onError: (error: any) => {
      console.error("Error fetching customer by ID:", error);
      enqueueSnackbar("เกิดข้อผิดพลาดในการโหลดข้อมูลลูกค้า", { variant: "error" });
    },
  });
};

// Get customer by customer code
export const useGetCustomerByCode = (customerCode: string, enabled: boolean = true) => {
  const { enqueueSnackbar } = useSnackbar();

  return useQuery(
    [EQueryKeys.GET_CUSTOMER_BY_CODE, customerCode],
    () => APIGetCustomerByCode(customerCode),
    {
      enabled: enabled && !!customerCode,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      onError: (error: any) => {
        console.error("Error fetching customer by code:", error);
        enqueueSnackbar("เกิดข้อผิดพลาดในการค้นหาลูกค้า", { variant: "error" });
      },
    }
  );
};

// Get customers by franchise UUID
export const useGetCustomersByFranchise = (franchiseUuid: string, enabled: boolean = true) => {
  const { enqueueSnackbar } = useSnackbar();

  return useQuery(
    [EQueryKeys.GET_CUSTOMERS_BY_FRANCHISE, franchiseUuid],
    () => APIGetCustomersByFranchise(franchiseUuid),
    {
      enabled: enabled && !!franchiseUuid,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      onError: (error: any) => {
        console.error("Error fetching customers by franchise:", error);
        enqueueSnackbar("เกิดข้อผิดพลาดในการโหลดข้อมูลลูกค้าตามแฟรนไชส์", { variant: "error" });
      },
    }
  );
};

// Create customer mutation
export const useCreateCustomer = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation((data: ICustomerCreateRequest) => APICreateCustomer(data), {
    onSuccess: (newCustomer: ICustomer) => {
      // Invalidate and refetch customers list
      queryClient.invalidateQueries([EQueryKeys.GET_CUSTOMERS]);
      queryClient.invalidateQueries([EQueryKeys.GET_CUSTOMERS_BY_FRANCHISE]);

      enqueueSnackbar("สร้างลูกค้าใหม่สำเร็จ", { variant: "success" });
    },
    onError: (error: any) => {
      console.error("Error creating customer:", error);
      const errorMessage = error?.response?.data?.message || "เกิดข้อผิดพลาดในการสร้างลูกค้า";
      enqueueSnackbar(errorMessage, { variant: "error" });
    },
  });
};

// Update customer mutation
export const useUpdateCustomer = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation(
    ({ uuid, data }: { uuid: string; data: ICustomerUpdateRequest }) =>
      APIUpdateCustomer(uuid, data),
    {
      onSuccess: (updatedCustomer: ICustomer) => {
        // Invalidate and refetch customers list and specific customer
        queryClient.invalidateQueries([EQueryKeys.GET_CUSTOMERS]);
        queryClient.invalidateQueries([EQueryKeys.GET_CUSTOMER_BY_ID, updatedCustomer.uuid]);
        queryClient.invalidateQueries([
          EQueryKeys.GET_CUSTOMER_BY_CODE,
          updatedCustomer.customer_code,
        ]);
        queryClient.invalidateQueries([EQueryKeys.GET_CUSTOMERS_BY_FRANCHISE]);

        enqueueSnackbar("อัปเดตลูกค้าสำเร็จ", { variant: "success" });
      },
      onError: (error: any) => {
        console.error("Error updating customer:", error);
        const errorMessage = error?.response?.data?.message || "เกิดข้อผิดพลาดในการอัปเดตลูกค้า";
        enqueueSnackbar(errorMessage, { variant: "error" });
      },
    }
  );
};

// Delete customer mutation
export const useDeleteCustomer = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation((uuid: string) => APIDeleteCustomer(uuid), {
    onSuccess: () => {
      // Invalidate and refetch customers list
      queryClient.invalidateQueries([EQueryKeys.GET_CUSTOMERS]);
      queryClient.invalidateQueries([EQueryKeys.GET_CUSTOMERS_BY_FRANCHISE]);

      enqueueSnackbar("ลบลูกค้าสำเร็จ", { variant: "success" });
    },
    onError: (error: any) => {
      console.error("Error deleting customer:", error);
      const errorMessage = error?.response?.data?.message || "เกิดข้อผิดพลาดในการลบลูกค้า";
      enqueueSnackbar(errorMessage, { variant: "error" });
    },
  });
};
